import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { createColumns, AdvertisementAction } from './columns'
import { DataTable } from './data-table'
import { useAdvertisementsBySupplierId } from '@/hooks/useAdvertisementsBySupplierId'
import { CreateAdvertisementDialog } from './CreateAdvertisementDialog'
import { useViewMode } from '@/stores/viewMode'
import { advertisementService } from '@/services/advertisement_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { logger } from '@magnetar/utils'
import { DeleteAlert } from '@/components/DeleteAlert'

export default function AdSupplierAdsPage() {
  logger.log('AdSupplierAdsPage')
  const navigate = useNavigate()
  // 本頁只在 'ad' mode（使用者已進入某個 supplier）有意義；
  // 在其他 mode（例如 provider mode）viewMode.id 可能是 provider_id，
  // 若直接用會誤打成 `/api/ad-supplier/<provider_id>/advertisements`，造成 403。
  const { mode, id: viewModeId } = useViewMode()
  const supplierId = mode === 'ad' ? viewModeId : undefined
  const { token } = useAuth()

  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [deletingAdvertisement, setDeletingAdvertisement] = useState<{
    advertisementId: number
    supplierId: number
  } | null>(null)

  const { advertisements, pagination, mutate } = useAdvertisementsBySupplierId(
    supplierId,
    `?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}${
      search ? `&search=${search}` : ''
    }`,
  )

  // Handle sorting change from data-table
  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field)
    setSortOrder(order)
    // Reset to page 1 when sorting changes
    setPage(1)
  }

  // Handle advertisement actions (edit/delete)
  const handleAction = (action: AdvertisementAction) => {
    if (action.type === 'delete') {
      setDeletingAdvertisement({
        advertisementId: action.advertisementId,
        supplierId: action.supplierId,
      })
    }
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingAdvertisement || !token) return

    try {
      const result = await advertisementService.deleteAdvertisement(
        deletingAdvertisement.supplierId,
        deletingAdvertisement.advertisementId,
        token,
      )

      if (result.success) {
        toast.success('廣告已刪除')
        // Refresh the data
        mutate()
      } else {
        toast.error(result.error?.message || '刪除廣告失敗')
      }
    } catch (error) {
      logger.error('刪除廣告失敗:', error)
      toast.error('刪除廣告失敗，請稍後再試')
    } finally {
      setDeletingAdvertisement(null)
    }
  }

  // if (isError) {
  //   return (
  //     <div>
  //       <div className="text-center text-destructive">載入廣告列表失敗。請稍後再試。</div>
  //     </div>
  //   )
  // }

  // if (isLoading) {
  //   return (
  //     <div>
  //       <Skeleton className="h-8 w-[200px] mb-4" />
  //       <Skeleton className="h-[400px] w-full" />
  //     </div>
  //   )
  // }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  // 若未處於 'ad' mode，提示使用者先選一家供應商
  if (!supplierId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">廣告管理</h2>
          <p className="text-muted-foreground mt-2">管理供應商廣告</p>
        </div>
        <Card>
          <CardContent className="p-6 flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              請先從供應商列表選擇一家供應商，才能管理其廣告。
            </p>
            <Button onClick={() => navigate(-1)}>前往供應商列表</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">廣告管理</h2>
          <p className="text-muted-foreground mt-2">管理供應商廣告</p>
        </div>
        <CreateAdvertisementDialog onRefresh={mutate} />
      </div>

      <div className="h-4" />

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={createColumns({ onAction: handleAction, onRefresh: mutate })}
            data={advertisements ?? []}
            onSearch={setSearch}
            onSort={handleSortingChange}
            initialSort={{ id: sortBy, desc: sortOrder === 'desc' }}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 1) setPage(page - 1)
                      }}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) => {
                    if (item === 'ellipsis') {
                      return (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )
                    }

                    return (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href="#"
                          isActive={item === page}
                          onClick={(e) => {
                            e.preventDefault()
                            setPage(item as number)
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (pagination && page < pagination.totalPages) setPage(page + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 刪除確認對話框 */}
      <DeleteAlert
        open={deletingAdvertisement !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingAdvertisement(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="確定要刪除此廣告嗎？"
        description="此操作無法復原。這將永久刪除此廣告和所有相關資料。"
      />
    </div>
  )
}
